import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import BetSlip from '../../BetSlip';
import CricketSchedule from '../../Today';
import SportsScheduleTomorrow from '../../Tomorrow';
import CricketBettingUI from '..';
import TabSwitcher from '../../TabSwitcher';
import MiddleDetailBettingUi from './Middle';
import LiveStreamFrame from '../../LiveStreamFrame';
import { API } from '../../../../../../Helper';
import BettingTable from '../../MyBets';
import BettingHistoryTable from '../../BettingHistoryTable';
import { useBetting } from '../../../../../../Context/BettingContext';
import { ExposureProvider } from '../../../../../../hooks/useExposure';
import SideMenu, { SPORT_ID_MAP } from './SideMenu';
import SportsBanner from '../../../../../../App/Components/sportbanner/sportbanner.js';
import { useCallback } from 'react';

// Styled Components
const LayoutContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  font-family: 'Montserrat Alternates', sans-serif;
  background-color: #292d2e;
`;

const MainContent = styled.div`
  flex: 0 0 55%;
  background-color: #292d2e;
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  margin-bottom: 19px;
  font-family: 'Montserrat Alternates', sans-serif;
  border-bottom: 1px solid rgb(240, 156, 6);
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-height: calc(100vh - 200px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background-color: #292d2e;
  font-family: 'Montserrat Alternates', sans-serif;

  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgb(240, 156, 6);
    border-radius: 2px;
  }
`;

const Header = styled.div`
  background: #292d2e;
  position: relative;
  width: 100%;
  height: 200px;
margin-left: 10px;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const BannerImage = styled.img`
  width: 100%
  height: 100%;
  object-fit: cover;
  object-position: center;
  // transition: transform 0.5s ease;
  
`;

const BannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  // background: rgba(0, 0, 0, 0.7);
  pointer-events: none;
`;

const Section = styled.div`
  flex: ${props => props.width};
  background-color: #292d2e;
  min-height: 100vh;
  height: 100vh;
  // border: ${props => props.borderLeft ? `1px solid ${props.borderLeft}` : 'none'};
  overflow-x: hidden;
  overflow-y: ${props => (props.scrollable ? 'auto' : 'hidden')};
  border-right: 1px solid rgb(240, 156, 6);
  font-family: 'Montserrat Alternates', sans-serif;

  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgb(240, 156, 6);
    border-radius: 2px;
  }
`;

const TabContent = styled.div`
  animation: fadeIn 0.3s ease;
  flex: 1;
  margin-bottom: 12px;
  min-height: calc(100vh - 300px);
  overflow-y: auto;
  background-color: #292d2e;
  font-family: 'Montserrat Alternates', sans-serif;
  z-index: 1;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgb(240, 156, 6);
    border-radius: 2px;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const MatchTitle = styled.div`
  color: #ffffff;
  font-size: 10px;
  padding: 16px;
  text-align: center;
  margin-bottom: 12px;
  flex-shrink: 0;
  font-family: 'Montserrat Alternates', sans-serif;
  background-color: rgb(50, 55, 56);
`;

const MatchTitle2 = styled.div`
  color: rgb(240, 156, 6);
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  flex-shrink: 0;
  font-family: 'Montserrat Alternates', sans-serif;
`;

// ---------- Simple in-memory cache with stale-while-revalidate & dedupe ----------
/**
 * Cache semantics:
 * - cache.data: Map<string, { value: any, timestamp: number }>
 * - cache.inFlight: Map<string, Promise> -> dedupe in-flight requests
 *
 * fetchWithCache(key, fetcher, { ttl })
 *  - if fresh (now - timestamp < ttl) -> resolve cached value immediately
 *  - if stale but exist -> return cached value immediately and trigger background refresh
 *  - if no cached value -> await fetcher, store, return
 */
const cache = {
  data: new Map(),
  inFlight: new Map(),
};

const DEFAULT_TTL = 10 * 1000; // 10 seconds (tuneable)

async function fetchWithCache(key, fetcher, options = {}) {
  const ttl = options.ttl ?? DEFAULT_TTL;
  const now = Date.now();
  const cached = cache.data.get(key);

  // If there's an in-flight request, return that promise (dedupe)
  const inFlight = cache.inFlight.get(key);
  if (inFlight) {
    // If we have an immediate cached value, also return it as { immediate, promise }
    return inFlight;
  }

  if (cached) {
    const age = now - cached.timestamp;
    if (age < ttl) {
      // fresh -> return resolved promise with the cached value
      return Promise.resolve(cached.value);
    } else {
      // stale -> return cached immediately but refresh in background
      // start a background fetch but return cached value immediatelly to caller
      backgroundRefresh(key, fetcher);
      return Promise.resolve(cached.value);
    }
  }

  // no cache -> fetch, cache and return
  const p = (async () => {
    try {
      const v = await fetcher();
      cache.data.set(key, { value: v, timestamp: Date.now() });
      return v;
    } finally {
      cache.inFlight.delete(key);
    }
  })();

  cache.inFlight.set(key, p);
  return p;
}

function backgroundRefresh(key, fetcher) {
  if (cache.inFlight.get(key)) return; // already refreshing
  const p = (async () => {
    try {
      const v = await fetcher();
      cache.data.set(key, { value: v, timestamp: Date.now() });
      return v;
    } catch (e) {
      // ignore background errors, keep stale cache
      console.error('Background refresh failed for key', key, e);
    } finally {
      cache.inFlight.delete(key);
    }
  })();
  cache.inFlight.set(key, p);
  return p;
}
// -------------------------------------------------------------------------------

// Memoized Menu Component
const CricketBetUiComponent = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();
  const { tab } = useParams();
  const [activeTab, setActiveTab] = useState('in-play');
  const [sideMenutab, setSideMenuTab] = useState('all-sports');
  const matchDataIS = location.state?.matchData;
  const { isMatchClicked } = useBetting();

  useEffect(() => {
    if (matchDataIS) {
      setIsLoading(false);
    }
  }, [matchDataIS, tab, isMatchClicked]);

  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
  }, [tab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  const handleSideTabChange = (tab) => {
    setSideMenuTab(tab);
  };

  const [shouldShowLiveStream, setShouldShowLiveStream] = useState(true);
  const [inplayMarkets, setInplayMarkets] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    const excludedPaths = [
      '/sports/markets/in-play',
      '/sports/markets/today',
      '/sports/markets/tomorrow',
      '/sports/markets/history',
    ];
    setShouldShowLiveStream(!excludedPaths.includes(path));
  }, [location.pathname]);

  // Helper fetchers
  const fetchAllInplay = useCallback(async () => {
    const res = await fetch(`${API}/sports/allinplay`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json;
  }, []);

  const fetchAllMatches = useCallback(async () => {
    const res = await fetch('https://bkk07.com/sports/all-matches', {
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json;
  }, []);

  const fetchGamesBySportId = useCallback(async (id) => {
    const res = await fetch(`${API}/sports/inplayGameId/${id}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await res.json();
    return json;
  }, []);

  // Core: fetchInplayBySport uses cache above to avoid repeated loads
  const fetchInplayBySport = useCallback(
    async (selectedTabId) => {
      setIsFetching(true);
      try {
        let markets = [];

        if (selectedTabId === 'all-sports') {
          // Use combined cached endpoints with a short TTL - stale-while-revalidate pattern
          const keyInplay = 'allinplay';
          const keyAll = 'allmatches';

          // We will fetch both via cache-with-refresh
          // Both fetches may return cached immediately (stale or fresh) or wait for fetch
          const [inPlayData, allMatchesData] = await Promise.all([
            fetchWithCache(keyInplay, fetchAllInplay, { ttl: 10_000 }),
            fetchWithCache(keyAll, fetchAllMatches, { ttl: 10_000 }),
          ]);

          const collectMarkets = (arr) =>
            Array.isArray(arr) ? arr.flatMap((s) => s?.markets || []) : [];

          const inPlayMarkets = collectMarkets(inPlayData).map((m) => ({ ...m, inplay: true }));
          const allMarkets = collectMarkets(allMatchesData);

          // merge with in-play preferred
          const map = new Map();
          inPlayMarkets.forEach((m) => m?.marketId && map.set(m.marketId, m));
          allMarkets.forEach((m) => {
            if (m?.marketId && !map.has(m.marketId)) map.set(m.marketId, m);
          });
          markets = Array.from(map.values());
        } else {
          // handle specific sport: use cache per sport
          const sportId = SPORT_ID_MAP[selectedTabId];
          if (!sportId) {
            setInplayMarkets([]);
            return;
          }
          const key = `sport:${sportId}`;
          // fetchWithCache will return cached result or fetch new
          const res = await fetchWithCache(key, () => fetchGamesBySportId(sportId), { ttl: 10_000 });
          // res may be the json object or array - keep compatibility with original mapping
          const games = res?.games || res || [];
          const mkt = Array.isArray(games) ? games.flatMap((g) => g?.markets || []) : [];
          markets = mkt.map((m) => ({ ...m, inplay: true }));
        }

        // map markets to lightweight list for UI
        const mapped = markets.map((m) => ({
          id: m.marketId,
          team1: m.runners?.[0]?.runnerName || '',
          team2: m.runners?.[1]?.runnerName || '',
          inplay: Boolean(m.inplay),
          time: m.marketStartTime,
        }));
        setInplayMarkets(mapped);
      } catch (e) {
        console.error('Failed to fetch inplay markets (web)', e);
        setInplayMarkets([]);
      } finally {
        setIsFetching(false);
      }
    },
    [fetchAllInplay, fetchAllMatches, fetchGamesBySportId]
  );

  // refetch when activeTab or sideMenutab changes
  useEffect(() => {
    if (activeTab === 'in-play') {
      // Try to get a cached result fast and set it immediately (if available)
      // fetchInplayBySport already performs cached reads, so call it
      fetchInplayBySport(sideMenutab);
    }
  }, [activeTab, sideMenutab, fetchInplayBySport]);

  // Avoid duplicate effect definitions (you had repeated useEffects for same purpose before)
  // useMemo for middle and right sections to avoid unnecessary re-renders
  const { matchData } = location.state || {};
  const renderMiddleContent = useCallback(() => {
    switch (activeTab) {
      case 'in-play':
      case 'today':
      case 'tomorrow':
        return (
          <TabContent>
            <CricketBettingUI upperTab={activeTab} tab={sideMenutab} />
          </TabContent>
        );
      case 'history':
        return (
          <TabContent>
            <BettingHistoryTable />
          </TabContent>
        );
      default:
        return (
          <TabContent>
            <MiddleDetailBettingUi matchData={matchData} />
          </TabContent>
        );
    }
  }, [activeTab, sideMenutab, matchData]);

  const middleSection = useMemo(
    () => (
      <Section width="0 0 70%" bg="#292d2e">
        {renderMiddleContent()}
      </Section>
    ),
    [activeTab, sideMenutab, renderMiddleContent]
  );

  const rightSection = useMemo(
    () => (
      <Section borderLeft="rgb(240, 156, 6)" width="0 0 30%" scrollable>
        {shouldShowLiveStream && <LiveStreamFrame id={matchDataIS?.eventId} />}
        <BetSlip />
        <BettingTable />
      </Section>
    ),
    [shouldShowLiveStream, matchDataIS?.eventId]
  );

  const rightSectionHistory = useMemo(
    () => (
      <Section width="0 0 30%" scrollable>
        <LiveStreamFrame />
        <BetSlip />
      </Section>
    ),
    []
  );

  useEffect(() => {
    if (!matchData) {
      // optionally, redirect or show a message
    }
  }, [matchData]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    history.push(`/sports/markets/${tab}`);
  };

  // Optional: expose a manual refresh that forces background refresh
  const manualRefreshInplay = useCallback(() => {
    // clear cache entry for current tab to force fresh fetch next time
    const key = sideMenutab === 'all-sports' ? 'allinplay' : `sport:${SPORT_ID_MAP[sideMenutab]}`;
    // remove stale entry so next fetchWithCache will actually run network fetch
    cache.data.delete(key);
    fetchInplayBySport(sideMenutab);
  }, [sideMenutab, fetchInplayBySport]);

  return (
    <LayoutContainer>
      <SideMenu activeTab={sideMenutab} onTabChange={handleSideTabChange} />

      <MainContent>
        <TabSwitcher activeTab={activeTab} onTabClick={handleTabClick} />
        {!matchData && (
          <Header>
            <BannerImage src="/assets/images/sport.jpg" alt="Sports Banner" />
            <BannerOverlay />
          </Header>
        )}
        <div>
          <ContentWrapper
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              width: '100%',
            }}
          >
            {matchData && (
              <div
                style={{
                  padding: '12px',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  backgroundColor: 'rgb(50, 55, 56)',
                  borderBottom: '1px solid rgb(240, 156, 6)',
                }}
              >
                <MatchTitle2>Live Score</MatchTitle2>

                <MatchTitle2>
                  {matchData.team1} vs {matchData.team2}
                </MatchTitle2>
              </div>
            )}
            {renderMiddleContent()}
          </ContentWrapper>
        </div>
      </MainContent>

      {rightSection}
    </LayoutContainer>
  );
};

export default CricketBetUiComponent;